import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase';

/**
 * Standard envelope returned by every Clinio Edge Function.
 * Mirrors `_shared/errors.ts` on the server side.
 */
export type ApiSuccess<T> = { ok: true; data: T };
export type ApiError = {
  code:
    | 'unauthorized'
    | 'forbidden'
    | 'invalid-input'
    | 'not-found'
    | 'conflict'
    | 'rate-limited'
    | 'method-not-allowed'
    | 'stripe-not-configured'
    | 'stripe-not-onboarded'
    | 'clinio-pay-unavailable'
    | 'clinio-forms-unavailable'
    | 'internal-error';
  message: string;
  details?: unknown;
};
export type ApiFailure = { ok: false; error: ApiError };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export class ApiCallError extends Error {
  readonly code: ApiError['code'];
  readonly details?: unknown;
  constructor(error: ApiError) {
    super(error.message);
    this.code = error.code;
    this.details = error.details;
    this.name = 'ApiCallError';
  }
}

/**
 * Invoke a Clinio Edge Function and unwrap its `{ ok, data | error }` envelope.
 * Throws ApiCallError for any non-ok response so call sites can use try/catch.
 */
export async function callFunction<TInput extends Record<string, unknown>, TOutput>(
  name: string,
  body: TInput,
): Promise<TOutput> {
  const { data, error } = await supabase.functions.invoke<ApiResponse<TOutput>>(name, {
    body,
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const errBody = (await error.context.json().catch(() => null)) as ApiResponse<TOutput> | null;
      if (errBody && errBody.ok === false) {
        throw new ApiCallError(errBody.error);
      }
    }
    throw new ApiCallError({
      code: 'internal-error',
      message: error.message ?? 'Function call failed',
    });
  }

  if (!data) {
    throw new ApiCallError({ code: 'internal-error', message: 'Empty response' });
  }
  if (data.ok === false) {
    throw new ApiCallError(data.error);
  }
  return data.data;
}
